import SearchBarLocationType from "enums/searchLocationType";
import CompletionSuggestionType from "enums/completionSuggestionType";
import SearchBarResultCategory from "enums/searchResultCategory";
import PageNavigationAction from 'enums/pageNavigationAction';

/**
 * Constructs the base payload object for location data.
 * @param {Object} data - The input data containing location information.
 * @returns {Object} The base payload object.
 */
const getBasePayload = (data) => ({
  geoLocationState: data?.geoLocationState,
  marketId: data?.marketId,
  marketName: data?.marketName,
  marketState: data?.marketState,
  marketStateName: data?.marketStateName,
  originLatitude: data?.originLatitude,
  originLongitude: data?.originLongitude,
  value: data?.name
});

/**
 * Unified location type mapping that consolidates all necessary information
 * for each location type in a single structure.
 */
const LOCATION_TYPES = {
  [SearchBarLocationType.County.toString()]: {
    buildPayload: (data) => ({
      ...getBasePayload(data),
      county: data?.name,
      locationType: SearchBarLocationType.County.toString()
    })
  },
  [SearchBarLocationType.Neighborhood.toString()]: {
    buildPayload: (data) => ({
      ...getBasePayload(data),
      city: data?.city,
      neighborhoodName: data?.name,
      locationType: SearchBarLocationType.Neighborhood.toString()
    })
  },
  [SearchBarLocationType.Zip.toString()]: {
    buildPayload: (data) => ({
      ...getBasePayload(data),
      city: data?.city,
      zip: data?.zip,
      locationType: SearchBarLocationType.Zip.toString()
    })
  },
  [SearchBarLocationType.City.toString()]: {
    buildPayload: (data) => ({
      ...getBasePayload(data),
      city: data?.city,
      locationType: SearchBarLocationType.City.toString()
    })
  },
  [SearchBarLocationType.SchoolDistrict.toString()]: {
    buildPayload: (data) => ({
      ...getBasePayload(data),
      schoolDistrictId: data?.completionSuggestionId,
      schoolDistrictName: data?.name,
      locationType: SearchBarLocationType.SchoolDistrict.toString()
    })
  },
  [SearchBarLocationType.Market.toString()]: {
    buildPayload: (data) => ({
      ...getBasePayload(data),
      locationType: SearchBarLocationType.Market.toString()
    })
  },
  [SearchBarLocationType.State.toString()]: {
    buildPayload: (data) => ({
      ...getBasePayload(data),
      marketState: data?.geoLocationState,
      stateName: data?.name,
      locationType: SearchBarLocationType.State.toString()
    })
  }
};

const mapLocationType = new Map([
  [CompletionSuggestionType.County.toString(), SearchBarLocationType.County.toString()],
  [CompletionSuggestionType.Neighborhood.toString(), SearchBarLocationType.Neighborhood.toString()],
  [CompletionSuggestionType.Zip.toString(), SearchBarLocationType.Zip.toString()],
  [CompletionSuggestionType.City.toString(), SearchBarLocationType.City.toString()],
  [CompletionSuggestionType.SchoolDistrict.toString(), SearchBarLocationType.SchoolDistrict.toString()],
  [CompletionSuggestionType.Market.toString(), SearchBarLocationType.Market.toString()],
  [CompletionSuggestionType.State.toString(), SearchBarLocationType.State.toString()]
]);

/**
 * Build location payload based on the location type
 * @param {Object} data - The location data
 * @returns {Object|null} - The constructed payload or null if invalid
 */
const getRedirectAction = (data) => {
  const locationCategory = SearchBarResultCategory.Location.id;
  const canUpdateLocation = data.categoryType === locationCategory;
  const isArticle = CompletionSuggestionType.Article.toString() === data?.completionSuggestionType;

  if (!canUpdateLocation && data.locationUrl) {
    return { type: PageNavigationAction.redirectToPage.name, url: data.locationUrl, shouldOpenInNewTab: isArticle };
  }

  if (!data?.completionSuggestionType) {
    return null;
  }

  const locationType = mapLocationType.get(data.completionSuggestionType);

  if (locationType) {
    const payload = LOCATION_TYPES[locationType].buildPayload(data);
    return { type: PageNavigationAction.updateLocation.name, payload };
  }

  return null;
};

export default getRedirectAction;